import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { listingsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Plus, X, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

function CreateListingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categoryList, setCategoryList] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [industryList, setIndustryList] = useState([]);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [jobTitles, setJobTitles] = useState([]);
  const [leadFields, setLeadFields] = useState(['first_name', 'last_name', 'email', 'company_name', 'job_title']);
  const [pricingTiers, setPricingTiers] = useState([
    { name: 'Starter', quantity: 100, price: 2500, cpl: 25 },
    { name: 'Growth', quantity: 250, price: 5000, cpl: 20 },
    { name: 'Scale', quantity: 500, price: 7500, cpl: 15 },
  ]);
  const [deliverySla, setDeliverySla] = useState(7);
  const [replacementPolicy, setReplacementPolicy] = useState('100% replacement for invalid leads within 30 days');
  const [complianceNotes, setComplianceNotes] = useState('All leads are GDPR compliant with documented consent');
  const [consentMethod, setConsentMethod] = useState('Double opt-in via email verification');
  
  const [newJobTitle, setNewJobTitle] = useState('');

  useEffect(function() {
    listingsAPI.getCategories().then(function(response) {
      setCategoryList(response.data.categories || []);
      setRegionList(response.data.regions || []);
      setIndustryList(response.data.industries || []);
    }).catch(function(error) {
      console.error('Failed to fetch categories');
    });
  }, []);

  function handleAddJobTitle() {
    if (newJobTitle.trim() && jobTitles.indexOf(newJobTitle.trim()) === -1) {
      setJobTitles(jobTitles.concat([newJobTitle.trim()]));
      setNewJobTitle('');
    }
  }

  function handleRemoveJobTitle(titleToRemove) {
    const updated = [];
    for (let i = 0; i < jobTitles.length; i++) {
      if (jobTitles[i] !== titleToRemove) {
        updated.push(jobTitles[i]);
      }
    }
    setJobTitles(updated);
  }

  function toggleRegion(region) {
    if (selectedRegions.indexOf(region) >= 0) {
      const updated = [];
      for (let i = 0; i < selectedRegions.length; i++) {
        if (selectedRegions[i] !== region) updated.push(selectedRegions[i]);
      }
      setSelectedRegions(updated);
    } else {
      setSelectedRegions(selectedRegions.concat([region]));
    }
  }

  function toggleIndustry(industry) {
    if (selectedIndustries.indexOf(industry) >= 0) {
      const updated = [];
      for (let i = 0; i < selectedIndustries.length; i++) {
        if (selectedIndustries[i] !== industry) updated.push(selectedIndustries[i]);
      }
      setSelectedIndustries(updated);
    } else {
      setSelectedIndustries(selectedIndustries.concat([industry]));
    }
  }

  function toggleLeadField(field) {
    const required = ['first_name', 'last_name', 'email', 'company_name', 'job_title'];
    if (required.indexOf(field) >= 0) return;
    
    if (leadFields.indexOf(field) >= 0) {
      const updated = [];
      for (let i = 0; i < leadFields.length; i++) {
        if (leadFields[i] !== field) updated.push(leadFields[i]);
      }
      setLeadFields(updated);
    } else {
      setLeadFields(leadFields.concat([field]));
    }
  }

  function updatePricingTier(index, field, value) {
    const newTiers = pricingTiers.slice();
    newTiers[index] = { ...newTiers[index] };
    newTiers[index][field] = field === 'name' ? value : parseFloat(value) || 0;
    if (field === 'price' || field === 'quantity') {
      newTiers[index].cpl = newTiers[index].quantity > 0 
        ? Math.round((newTiers[index].price / newTiers[index].quantity) * 100) / 100
        : 0;
    }
    setPricingTiers(newTiers);
  }

  function handleSubmit(e) {
    e.preventDefault();
    
    if (!title || !description || !category) {
      toast.error('Please fill all required fields');
      return;
    }
    if (selectedRegions.length === 0) {
      toast.error('Please select at least one region');
      return;
    }
    if (selectedIndustries.length === 0) {
      toast.error('Please select at least one industry');
      return;
    }
    if (jobTitles.length === 0) {
      toast.error('Please add at least one job title');
      return;
    }
    
    setLoading(true);
    
    const formData = {
      title: title,
      description: description,
      category: category,
      regions: selectedRegions,
      industries: selectedIndustries,
      job_titles: jobTitles,
      lead_fields: leadFields,
      pricing_tiers: pricingTiers,
      delivery_sla_days: deliverySla,
      replacement_policy: replacementPolicy,
      compliance_notes: complianceNotes,
      consent_method: consentMethod,
    };
    
    listingsAPI.create(formData).then(function() {
      toast.success('Listing created! Pending admin approval.');
      navigate('/seller/dashboard');
    }).catch(function(error) {
      toast.error(error.response?.data?.detail || 'Failed to create listing');
    }).finally(function() {
      setLoading(false);
    });
  }

  const leadFieldOptions = ['first_name', 'last_name', 'email', 'phone', 'company_name', 'job_title', 'linkedin_url', 'company_size', 'industry'];
  const requiredFields = ['first_name', 'last_name', 'email', 'company_name', 'job_title'];

  function renderCategoryOptions() {
    const items = [];
    for (let i = 0; i < categoryList.length; i++) {
      items.push(<SelectItem key={categoryList[i]} value={categoryList[i]}>{categoryList[i]}</SelectItem>);
    }
    return items;
  }

  function renderRegionBadges() {
    const items = [];
    for (let i = 0; i < regionList.length; i++) {
      const region = regionList[i];
      const isSelected = selectedRegions.indexOf(region) >= 0;
      items.push(
        <Badge
          key={region}
          variant={isSelected ? 'default' : 'outline'}
          className="cursor-pointer"
          onClick={function() { toggleRegion(region); }}
        >
          {region}
        </Badge>
      );
    }
    return items;
  }

  function renderIndustryBadges() {
    const items = [];
    for (let i = 0; i < industryList.length; i++) {
      const industry = industryList[i];
      const isSelected = selectedIndustries.indexOf(industry) >= 0;
      items.push(
        <Badge
          key={industry}
          variant={isSelected ? 'default' : 'outline'}
          className="cursor-pointer"
          onClick={function() { toggleIndustry(industry); }}
        >
          {industry}
        </Badge>
      );
    }
    return items;
  }

  function renderJobTitleBadges() {
    const items = [];
    for (let i = 0; i < jobTitles.length; i++) {
      const title = jobTitles[i];
      items.push(
        <Badge key={title} variant="secondary" className="gap-1">
          {title}
          <X 
            className="w-3 h-3 cursor-pointer hover:text-red-500" 
            onClick={function() { handleRemoveJobTitle(title); }}
          />
        </Badge>
      );
    }
    return items;
  }

  function renderLeadFieldBadges() {
    const items = [];
    for (let i = 0; i < leadFieldOptions.length; i++) {
      const field = leadFieldOptions[i];
      const isRequired = requiredFields.indexOf(field) >= 0;
      const isSelected = leadFields.indexOf(field) >= 0;
      items.push(
        <Badge
          key={field}
          variant={isSelected ? 'default' : 'outline'}
          className={'cursor-pointer ' + (isRequired ? 'opacity-75' : '')}
          onClick={function() { toggleLeadField(field); }}
        >
          {field.replace(/_/g, ' ')}
          {isRequired && ' *'}
        </Badge>
      );
    }
    return items;
  }

  function renderPricingTiers() {
    const items = [];
    for (let i = 0; i < pricingTiers.length; i++) {
      const tier = pricingTiers[i];
      const index = i;
      items.push(
        <div key={tier.name} className="grid grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
          <div>
            <Label className="text-xs">Tier Name</Label>
            <Input
              value={tier.name}
              onChange={function(e) { updatePricingTier(index, 'name', e.target.value); }}
            />
          </div>
          <div>
            <Label className="text-xs">Lead Count</Label>
            <Input
              type="number"
              value={tier.quantity}
              onChange={function(e) { updatePricingTier(index, 'quantity', e.target.value); }}
            />
          </div>
          <div>
            <Label className="text-xs">Total Price ($)</Label>
            <Input
              type="number"
              value={tier.price}
              onChange={function(e) { updatePricingTier(index, 'price', e.target.value); }}
            />
          </div>
          <div>
            <Label className="text-xs">CPL ($)</Label>
            <Input value={tier.cpl} disabled className="bg-slate-100" />
          </div>
        </div>
      );
    }
    return items;
  }

  return (
    <Layout showFooter={false}>
      <div className="container mx-auto px-4 lg:px-8 py-8 max-w-4xl" data-testid="create-listing-page">
        <Button variant="ghost" className="mb-6" onClick={function() { navigate('/seller/dashboard'); }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Create New Listing</h1>
          <p className="text-slate-600">Define your lead offering and pricing</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Listing Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., IT Security Decision Makers - Nordics"
                  value={title}
                  onChange={function(e) { setTitle(e.target.value); }}
                  required
                  data-testid="listing-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your lead offering..."
                  value={description}
                  onChange={function(e) { setDescription(e.target.value); }}
                  rows={4}
                  required
                  data-testid="listing-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={category} onValueChange={function(v) { setCategory(v); }}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>{renderCategoryOptions()}</SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Targeting Options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="mb-2 block">Regions *</Label>
                <div className="flex flex-wrap gap-2">{renderRegionBadges()}</div>
              </div>
              <Separator />
              <div>
                <Label className="mb-2 block">Industries *</Label>
                <div className="flex flex-wrap gap-2">{renderIndustryBadges()}</div>
              </div>
              <Separator />
              <div>
                <Label className="mb-2 block">Job Titles *</Label>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Add job title..."
                    value={newJobTitle}
                    onChange={function(e) { setNewJobTitle(e.target.value); }}
                    onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); handleAddJobTitle(); } }}
                    data-testid="job-title-input"
                  />
                  <Button type="button" variant="outline" onClick={handleAddJobTitle}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">{renderJobTitleBadges()}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Lead Fields Provided</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">{renderLeadFieldBadges()}</div>
              <p className="text-xs text-slate-500 mt-2">* Required fields cannot be removed</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Pricing Tiers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">{renderPricingTiers()}</CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Delivery & Compliance</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Delivery SLA (days)</Label>
                  <Input
                    type="number"
                    value={deliverySla}
                    onChange={function(e) { setDeliverySla(parseInt(e.target.value) || 7); }}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Consent Method</Label>
                  <Input
                    value={consentMethod}
                    onChange={function(e) { setConsentMethod(e.target.value); }}
                    placeholder="e.g., Double opt-in"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Replacement Policy</Label>
                <Textarea
                  value={replacementPolicy}
                  onChange={function(e) { setReplacementPolicy(e.target.value); }}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Compliance Notes</Label>
                <Textarea
                  value={complianceNotes}
                  onChange={function(e) { setComplianceNotes(e.target.value); }}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={function() { navigate('/seller/dashboard'); }}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-listing-btn">
              {loading ? (
                <span className="flex items-center">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </span>
              ) : (
                <span className="flex items-center">
                  <Save className="w-4 h-4 mr-2" />
                  Create Listing
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default CreateListingPage;
